import {
  XMarkIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import type { IVideoUnderstandingJob, IVideoUnderstandingStep } from '@/services/videoUnderstandingApi';

export function StatusBadge({ status }: { status: IVideoUnderstandingJob['status'] }) {
  const colors: Record<string, string> = {
    queued: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    cancelled: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${colors[status] || colors.queued}`}>{status}</span>;
}

const STEP_LABELS: Record<IVideoUnderstandingStep['id'], string> = {
  download: 'Downloading video',
  transcribe: 'Transcribing audio',
  analyze: 'Analyzing transcript',
};

function stepIcon(status: IVideoUnderstandingStep['status']) {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
    case 'processing':
      return <ArrowPathIcon className="h-4 w-4 text-blue-500 animate-spin" />;
    case 'failed':
      return <ExclamationCircleIcon className="h-4 w-4 text-red-500" />;
    default:
      return <ClockIcon className="h-4 w-4 text-gray-300 dark:text-gray-600" />;
  }
}

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function VideoUnderstandingDetailsDrawer({
  job,
  onClose,
}: {
  job: IVideoUnderstandingJob;
  onClose: () => void;
}) {
  const isInProgress = job.status === 'queued' || job.status === 'processing';
  const hasResults = job.chapters.length > 0 || job.topics.length > 0 || !!job.summary || job.highlights.length > 0;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true" />
      <div className="relative h-full w-full md:w-[640px] lg:w-[760px] bg-white dark:bg-gray-900 shadow-2xl flex flex-col">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">Understanding</p>
            <h2 className="mt-0.5 text-lg font-semibold text-gray-900 dark:text-white truncate">{job.title || 'Untitled'}</h2>
            <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{job.status === 'processing' ? `${job.progress}% · ${job.currentStep}` : job.currentStep}</span>
              <StatusBadge status={job.status} />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {job.status === 'failed' && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-900/20">
              <p className="text-sm font-medium text-red-800 dark:text-red-300">Video understanding failed</p>
              {job.error && <p className="mt-1 text-sm text-red-700 dark:text-red-400">{job.error}</p>}
            </div>
          )}

          {(isInProgress || job.status === 'failed') && job.steps && job.steps.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">Pipeline</h3>
              <div className="space-y-2">
                {job.steps.map((step) => (
                  <div
                    key={step.id}
                    className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
                  >
                    {stepIcon(step.status)}
                    <span className="flex-1 text-sm text-gray-700 dark:text-gray-300">{STEP_LABELS[step.id]}</span>
                    {step.error && <span className="text-xs text-red-500 truncate max-w-[40%]">{step.error}</span>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {isInProgress && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-8 text-center">
              <ArrowPathIcon className="mx-auto h-6 w-6 animate-spin text-indigo-500" />
              <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-200">{job.currentStep || 'Processing'}…</p>
              <div className="mx-auto mt-3 h-1.5 w-48 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div className="h-full rounded-full bg-indigo-600 transition-all" style={{ width: `${job.progress || 0}%` }} />
              </div>
            </div>
          )}

          {job.summary && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">Summary</h3>
              <p className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {job.summary}
              </p>
            </section>
          )}

          {job.topics.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">Topics</h3>
              <div className="flex flex-wrap gap-2">
                {job.topics.map((topic, idx) => (
                  <span
                    key={idx}
                    className="rounded-full bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </section>
          )}

          {job.chapters.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                Chapters <span className="text-gray-300 dark:text-gray-600">({job.chapters.length})</span>
              </h3>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                {job.chapters.map((chapter, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="text-sm text-gray-800 dark:text-gray-200 truncate">{chapter.title}</span>
                    <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                      {formatTime(chapter.start)}–{formatTime(chapter.end)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {job.highlights.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                Highlights <span className="text-gray-300 dark:text-gray-600">({job.highlights.length})</span>
              </h3>
              <div className="space-y-2">
                {[...job.highlights]
                  .sort((a, b) => b.score - a.score)
                  .map((h, idx) => (
                    <div key={idx} className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white truncate">
                          <SparklesIcon className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                          {h.label || `Moment ${idx + 1}`}
                        </span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                            {formatTime(h.start)}–{formatTime(h.end)}
                          </span>
                          <span className="rounded-full bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                            {Math.round(h.score * 100)}%
                          </span>
                        </div>
                      </div>
                      {h.reason && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{h.reason}</p>}
                    </div>
                  ))}
              </div>
            </section>
          )}

          {job.transcriptSegments.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">Transcript</h3>
              <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                {job.transcriptSegments.map((seg, idx) => (
                  <div key={idx} className="flex gap-3 px-4 py-2 text-sm">
                    <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500 tabular-nums pt-0.5">{formatTime(seg.start)}</span>
                    <span className="text-gray-700 dark:text-gray-300">{seg.text}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {job.status === 'completed' && !hasResults && (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">No results were extracted from this video.</p>
          )}
        </div>
      </div>
    </div>
  );
}
